import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import CustomNavbar from "components/users/CustomNavbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faClock,
  faQuestionCircle,
  faPlus,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  customYellowColor,
  primaryColor,
  primaryColorOpacity,
  secondaryColor,
} from "utils/colors";
import { convertToBase64 } from "utils/imagesAndColors";
import logo from "assets/logo.png";
import ImageModal from "components/general/ImageModal";
import {
  createAuthenticatedAxiosInstance,
  fetchCategories,
  fetchCategoryProducts,
  fetchProducts,
  fetchPromotionById,
  updateProductPromotions,
  updatePromotion,
} from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import CustomMultiSelect, {
  OptionType,
} from "components/general/CustomMultiSelect";
import ConfirmationModal from "components/general/ConfirmationModal";
import CustomBackButton from "components/general/CustomBackButton";
import LoadingScreen from "components/general/LoadingScreen";
import { UpdatePromotionRequest } from "interfaces/IPromotion";
import { es } from 'date-fns/locale';
import "./EditPromotion.scss";

const EditPromotion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [promotionName, setPromotionName] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [discountValue, setDiscountValue] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState<OptionType[]>(
    []
  );
  const [selectedProducts, setSelectedProducts] = useState<OptionType[]>([]);
  const [image, setImage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showPercentage, setShowPercentage] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Hubo un error al actualizar la promoción. Por favor, inténtalo de nuevo."
  );
  const [deletedImages, setDeletedImages] = useState<string[]>([]);

  // Options for selects
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  const [errors, setErrors] = useState({
    name: false,
    startDateTime: false,
    endDateTime: false,
    discount: false,
    description: false,
    image: false,
    selection: false,
  });

  const startDateRef = useRef<DatePicker>(null);
  const startTimeRef = useRef<DatePicker>(null);
  const endDateRef = useRef<DatePicker>(null);
  const endTimeRef = useRef<DatePicker>(null);

  /**
   * Effect hook to fetch and load promotion data
   * @effect
   * @dependencies [id]
   *
   * The effect:
   * 1. Makes API call to fetch promotion by ID
   * 2. Updates all form states with promotion data:
   *    - Basic info (name, description, discount)
   *    - Parses and sets dates and times
   *    - Maps categories and products to select options
   * 3. Manages loading state
   * 4. Handles error cases
   */
  useEffect(() => {
    const loadData = async () => {
      try {
        const promotion = await fetchPromotionById(axiosInstance, Number(id));

        // Set initial form values
        setPromotionName(promotion.name);
        setDescription(promotion.description);
        setDiscountValue(promotion.discount.toString());
        setShowPercentage(promotion.type === "%");
        setStatus(promotion.status);
        setImage(promotion.images[0] || "");

        // Parse dates
        const startDateTime = new Date(promotion.start_date);
        const endDateTime = new Date(promotion.end_date);
        setStartDate(startDateTime);
        setStartTime(startDateTime);
        setEndDate(endDateTime);
        setEndTime(endDateTime);

        // Load categories and products
        const categoriesResponse = await fetchCategories(axiosInstance, {
          status: "true",
          page: 1,
          per_page: 100000,
        });
        const productsResponse = await fetchProducts(axiosInstance, {
          status: "true",
          page: 1,
          per_page: 100000,
        });

        setCategories(
          categoriesResponse.categories.map((cat) => ({
            value: cat.id.toString(),
            label: cat.name,
          }))
        );

        setProducts(
          productsResponse.products.map((prod) => ({
            value: prod.id.toString(),
            label: prod.name,
          }))
        );

        // Set selected categories and products
        setSelectedCategories(
          promotion.categories.map((category) => ({
            value: category.id.toString(),
            label: category.name,
          }))
        );

        setSelectedProducts(
          promotion.products.map((product) => ({
            value: product.id.toString(),
            label: product.name,
          }))
        );

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading promotion:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  /**
   * Handles image selection from file input
   * @function onImageSelect
   * @param {React.ChangeEvent<HTMLInputElement>} event - File input change event
   * @async
   *
   * Converts selected image to base64 and updates state
   */
  const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const base64Image = await convertToBase64(files[0]);
      setImage(base64Image);
    }
  };

  /**
   * Handles image deletion
   * @function handleDeleteImage
   *
   * Marks current image for deletion and clears image state
   */
  const handleDeleteImage = () => {
    if (image) {
      setDeletedImages([`${id}_img1.png`]);
    }
    setImage("");
  };

  /**
   * Handles form submission for promotion update
   * @function handleSubmit
   * @async
   *
   * The function:
   * 1. Validates all required fields
   * 2. Formats dates for API
   * 3. Prepares promotion data
   * 4. Updates promotion details
   * 5. Updates product associations
   * 6. Handles success/error states
   */
  const handleSubmit = async () => {
    setErrors({
      name: false,
      startDateTime: false,
      endDateTime: false,
      discount: false,
      description: false,
      image: false,
      selection: false,
    });

    const newErrors = {
      name: !promotionName.trim(),
      startDateTime: !startDate || !startTime,
      endDateTime: !endDate || !endTime,
      discount: !discountValue,
      description: !description.trim(),
      image: !image,
      selection:
        selectedCategories.length === 0 && selectedProducts.length === 0,
    };

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    const formatDate = (date: Date, time: Date) => {
      const formattedDate = new Date(date);
      formattedDate.setHours(time.getHours());
      formattedDate.setMinutes(time.getMinutes());
      formattedDate.setSeconds(0);

      return formattedDate
        .toLocaleString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
        .replace(",", "");
    };

    const promotionData: UpdatePromotionRequest = {
      name: promotionName,
      type: showPercentage ? "%" : "$",
      description: description,
      start_date: formatDate(startDate!, startTime!),
      end_date: formatDate(endDate!, endTime!),
      discount: parseFloat(discountValue),
      images: [image],
      deleted_images: deletedImages,
    };

    try {
      const response = await updatePromotion(
        axiosInstance,
        Number(id),
        promotionData
      );

      if (response.promotion) {
        let allProductIds: number[] = selectedProducts.map((p) =>
          parseInt(p.value)
        );

        // Add products from selected categories
        if (selectedCategories.length > 0) {
          const categoryIds = selectedCategories.map((c) => parseInt(c.value));
          const categoryProductsResponse = await fetchCategoryProducts(
            axiosInstance,
            categoryIds
          );
          allProductIds = Array.from(
            new Set([...allProductIds, ...categoryProductsResponse.product_ids])
          );
        }

        // Update product associations using the new endpoint
        await updateProductPromotions(axiosInstance, Number(id), {
          product_ids: allProductIds,
        });

        setShowSuccessModal(true);
      } else {
        setErrorMessage(
          "Hubo un error al actualizar la promoción. Por favor, inténtalo de nuevo."
        );
        setShowErrorModal(true);
      }
    } catch (error: any) {
      if (error.response?.status === 422) {
        setErrorMessage(
          "Este nombre ya está registrado, por favor intenta con uno diferente"
        );
      }
      setShowErrorModal(true);
    }
  };

  if (isLoading) {
    return (
      <LoadingScreen loadingText="Cargando información de la promoción..." />
    );
  }

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/dashboard#promociones")} />

      <Container className="px-4 px-md-5 promotion-edit">
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col className="text-center">
            <h1>Editar Promoción</h1>
          </Col>
        </Row>

        <Form>
          <Row className="justify-content-center mb-4">
            <Col md={8}>
              <Row className="mb-3">
                <Col xs={6}>
                  <Form.Label className="fw-bold">No.</Form.Label>
                  <div className="d-flex align-items-center">
                    <span style={{ fontSize: "24px", color: "#747775" }}>
                      {id}
                    </span>
                    <OverlayTrigger
                      trigger={["hover", "focus"]}
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          ID único de la promoción
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                    </OverlayTrigger>
                  </div>
                </Col>
                <Col xs={6} className="text-end">
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Estado actual de la promoción
                      </Tooltip>
                    }
                  >
                    <span
                      className={`status-badge ${
                        status ? "active" : "inactive"
                      }`}
                    >
                      {status ? "Activo" : "Inactivo"}
                    </span>
                  </OverlayTrigger>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="justify-content-center mb-1">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold w-100">
                  Nombre de la promoción:
                </Form.Label>
                <Form.Control
                  type="text"
                  value={promotionName}
                  onChange={(e) => setPromotionName(e.target.value)}
                  placeholder="Nombre de la promoción"
                  isInvalid={errors.name}
                />
                {errors.name && (
                  <Form.Control.Feedback type="invalid">
                    El nombre de la promoción es requerido
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8}>
              <Card
                style={{
                  backgroundColor: primaryColorOpacity,
                  border: `2px solid ${
                    errors.startDateTime || errors.endDateTime
                      ? "red"
                      : primaryColor
                  }`,
                  boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.4)",
                  margin: "1rem 0rem",
                }}
              >
                <Card.Body>
                  <Row className="justify-content-center">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          Fecha inicio
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <DatePicker
                            locale={es}
                            ref={startDateRef}
                            selected={startDate}
                            onChange={(date) => {
                              setStartDate(date);
                              if (endDate && date && date > endDate) {
                                setEndDate(null);
                                setEndTime(null);
                              }
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="00/00/0000"
                            className="form-control"
                            minDate={new Date()}
                          />
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="ms-2"
                            onClick={() => startDateRef.current?.setOpen(true)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Hora inicio</Form.Label>
                        <div className="d-flex align-items-center">
                          <DatePicker
                            locale={es}
                            ref={startTimeRef}
                            selected={startTime}
                            onChange={(time) => {
                              setStartTime(time);
                              if (
                                startDate?.toDateString() ===
                                  endDate?.toDateString() &&
                                endTime &&
                                time &&
                                time > endTime
                              ) {
                                setEndTime(null);
                              }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            placeholderText="00:00 AM"
                            className="form-control"
                            minTime={
                              startDate?.toDateString() ===
                              new Date().toDateString()
                                ? new Date(
                                    new Date().setMinutes(
                                      new Date().getMinutes() + 15
                                    )
                                  )
                                : new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            maxTime={
                              new Date(new Date().setHours(23, 59, 0, 0))
                            }
                          />
                          <FontAwesomeIcon
                            icon={faClock}
                            className="ms-2"
                            onClick={() => startTimeRef.current?.setOpen(true)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Fecha fin</Form.Label>
                        <div className="d-flex align-items-center">
                          <DatePicker
                            locale={es}
                            ref={endDateRef}
                            selected={endDate}
                            onChange={setEndDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="00/00/0000"
                            className="form-control"
                            minDate={startDate || new Date()}
                          />
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="ms-2"
                            onClick={() => endDateRef.current?.setOpen(true)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Hora fin</Form.Label>
                        <div className="d-flex align-items-center">
                          <DatePicker
                            locale={es}
                            ref={endTimeRef}
                            selected={endTime}
                            onChange={setEndTime}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="h:mm aa"
                            placeholderText="00:00 AM"
                            className="form-control"
                            minTime={
                              startDate?.getTime() === endDate?.getTime()
                                ? startTime
                                  ? new Date(
                                      new Date(startTime).setMinutes(
                                        startTime.getMinutes() + 15
                                      )
                                    )
                                  : new Date(new Date().setHours(0, 0, 0, 0))
                                : new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            maxTime={
                              new Date(new Date().setHours(23, 59, 0, 0))
                            }
                          />
                          <FontAwesomeIcon
                            icon={faClock}
                            className="ms-2"
                            onClick={() => endTimeRef.current?.setOpen(true)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              {errors.startDateTime && (
                <div className="text-danger">
                  La fecha y hora de inicio son requeridas
                </div>
              )}
              {errors.endDateTime && (
                <div className="text-danger">
                  La fecha y hora de fin son requeridas
                </div>
              )}
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={6}>
              <Form.Group>
                <Row className="justify-content-center">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-center w-100">
                        Descuento:
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip className="custom-tooltip">
                              Elige si el tipo de promoción que deseas es de
                              porcentaje o de cantidad
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            icon={faQuestionCircle}
                            className="ms-2"
                          />
                        </OverlayTrigger>
                      </Form.Label>
                      <div className="d-flex justify-content-center">
                        <div className="d-flex rounded overflow-hidden">
                          {!showPercentage && (
                            <div
                              className="px-2 py-1 text-white"
                              onClick={() => setShowPercentage(true)}
                              style={{
                                cursor: "pointer",
                                backgroundColor: secondaryColor,
                                borderTopLeftRadius: "1rem",
                                borderBottomLeftRadius: "1rem",
                              }}
                            >
                              <span className="fw-bold">$</span>
                            </div>
                          )}
                          <Form.Control
                            value={discountValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*\.?\d{0,2}$/.test(value)) {
                                const numericValue = parseFloat(value);
                                if (showPercentage) {
                                  if (
                                    value === "" ||
                                    (numericValue >= 0 && numericValue <= 100)
                                  ) {
                                    setDiscountValue(value);
                                  }
                                } else {
                                  if (value === "" || numericValue >= 0) {
                                    setDiscountValue(value);
                                  }
                                }
                              }
                            }}
                            style={{
                              width: "4.5rem",
                              borderRadius: 0,
                              backgroundColor: customYellowColor,
                              textAlign: "center",
                              height: "2rem",
                              padding: "0.25rem",
                            }}
                            placeholder="0.00"
                          />
                          {showPercentage && (
                            <div
                              className="px-2 py-1 text-white"
                              onClick={() => setShowPercentage(false)}
                              style={{
                                cursor: "pointer",
                                backgroundColor: secondaryColor,
                                borderTopRightRadius: "1rem",
                                borderBottomRightRadius: "1rem",
                              }}
                            >
                              <span className="fw-bold">%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {errors.discount && (
                        <div className="text-danger">
                          El descuento es requerido
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Descripción:
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Descripción de la promoción
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descripción de la promoción"
                  isInvalid={errors.description}
                />
                {errors.description && (
                  <div className="text-danger">La descripción es requerida</div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Categorías aplicables
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Selección de categorías a las que aplica la promoción
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
                <CustomMultiSelect
                  options={categories}
                  value={selectedCategories}
                  onChange={(newValue) =>
                    setSelectedCategories(newValue as OptionType[])
                  }
                  placeholder="Seleccionar categorías..."
                  zIndex={4}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Productos aplicables:
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Selección de productos a los que aplica la promoción
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
                <CustomMultiSelect
                  options={products}
                  value={selectedProducts}
                  onChange={(newValue) =>
                    setSelectedProducts(newValue as OptionType[])
                  }
                  placeholder="Seleccionar productos..."
                  zIndex={3}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8}>
              {errors.selection && (
                <div className="text-danger">
                  Debe seleccionar al menos una categoría o un producto
                </div>
              )}
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Imagen promocional:
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Selección de la imagen que representará la promoción
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
                <div className="d-flex justify-content-center">
                  {image ? (
                    <div
                      style={{
                        width: "9.5rem",
                        height: "9.5rem",
                        borderRadius: "9px",
                        overflow: "hidden",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={() => setModalOpen(true)}
                    >
                      <img
                        src={
                          image.startsWith("data:image")
                            ? image
                            : image.startsWith("http")
                            ? image
                            : `data:image/jpeg;base64,${image}`
                        }
                        alt="Promotion"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        variant="link"
                        style={{
                          position: "absolute",
                          top: "0.1rem",
                          right: "0.1rem",
                          color: "#614900",
                          borderRadius: "50%",
                          padding: "8px",
                          fontSize: "1.2rem",
                          zIndex: 1,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage();
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  ) : (
                    <label style={{ cursor: "pointer" }}>
                      <div
                        style={{
                          border: "2px solid" + secondaryColor,
                          borderRadius: "9px",
                          backgroundColor: secondaryColor,
                          width: "9.5rem",
                          height: "9.5rem",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: primaryColor,
                            borderRadius: "50%",
                            width: "8rem",
                            height: "8rem",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            color="white"
                            style={{ height: "7rem", fontWeight: "lighter" }}
                          />
                        </div>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={onImageSelect}
                          style={{ display: "none" }}
                        />
                      </div>
                    </label>
                  )}
                </div>
                {errors.image && (
                  <div className="text-danger text-center">
                    La imagen es requerida
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="justify-content-center mb-5">
            <Col md={8}>
              <Row className="justify-content-between">
                <Col xs="auto">
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/promotions/${id}`)}
                  >
                    Cancelar
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button variant="primary" onClick={handleSubmit}>
                    Guardar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Container>

      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={[image]}
        selectedIndex={0}
        onSelectImage={() => {}}
        isReadOnly={true}
        isSingleImage={true}
      />

      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/dashboard#promociones");
        }}
        title="Éxito"
        message="La promoción se ha actualizado exitosamente"
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        icon={faTimesCircle}
        iconColor="text-danger"
      />
    </>
  );
};

export default EditPromotion;
